import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { PROJECT_LOGOS } from "@/data/logos";

export const EASE = [0.22, 1, 0.36, 1];

export function Reveal({ children, delay = 0, y = 24, className = "", ...rest }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay, ease: EASE }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export function Eyebrow({ children }) {
  return (
    <p className="text-xs font-bold tracking-[0.22em] uppercase text-tnn-purple">{children}</p>
  );
}

export function PageHero({ eyebrow, title, lead, testid }) {
  return (
    <section data-testid={testid} className="bg-tnn-warm">
      <div className="max-w-6xl mx-auto px-6 pt-36 lg:pt-44 pb-14 lg:pb-20">
        <Reveal>
          <Eyebrow>{eyebrow}</Eyebrow>
        </Reveal>
        <Reveal delay={0.08}>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl font-bold tracking-tight text-tnn-ink max-w-3xl leading-[1.12]">
            {title}
          </h1>
        </Reveal>
        {lead && (
          <Reveal delay={0.16}>
            <p className="mt-6 text-base sm:text-lg text-tnn-muted leading-relaxed max-w-2xl">{lead}</p>
          </Reveal>
        )}
      </div>
    </section>
  );
}

export function CountUp({ to, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    let timer;
    let cancelled = false;
    const run = () => {
      const start = performance.now();
      const dur = 1800;
      const tick = (t) => {
        if (cancelled) return;
        const p = Math.min(1, (t - start) / dur);
        setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
        if (p < 1) {
          raf = requestAnimationFrame(tick);
        } else {
          timer = setTimeout(() => {
            if (cancelled) return;
            setVal(0);
            run();
          }, 5000);
        }
      };
      raf = requestAnimationFrame(tick);
    };
    run();
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [inView, to]);
  return (
    <span ref={ref}>
      {val.toLocaleString("en-US")}
      {suffix}
    </span>
  );
}

export function ArrowButton({ to, children, variant = "solid", testid, external = false, className = "" }) {
  const styles = {
    solid: "bg-tnn-purple text-white hover:bg-tnn-deep shadow-sm",
    white: "bg-white text-tnn-deep hover:bg-tnn-lilac",
    outlineLight: "border border-white/40 text-white hover:bg-white/10",
    outlineDark: "bg-white border border-tnn-ink/15 text-tnn-ink shadow-sm hover:border-tnn-purple hover:text-tnn-purple",
    ghost: "text-tnn-purple hover:text-tnn-deep px-0 py-0",
  };
  const cls = `group inline-flex items-center gap-2 font-semibold text-sm px-6 py-3 rounded-full transition-colors duration-200 ${styles[variant]} ${className}`;
  const icon =
    variant === "ghost" ? (
      <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
    ) : (
      <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    );
  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" data-testid={testid} className={cls}>
        {children} {icon}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testid} className={cls}>
      {children} {icon}
    </Link>
  );
}

export function ProjectVisual({ project, large = false }) {
  const logo = PROJECT_LOGOS[project.slug];
  if (logo) {
    return (
      <div
        className={`relative w-full flex items-center justify-center ${large ? "h-60" : "h-44"}`}
        style={{ background: logo.bg || "#ffffff" }}
      >
        <img
          src={logo.src}
          alt={`${project.name} logo`}
          loading="lazy"
          className={`object-contain ${large ? logo.hLg : logo.h}`}
        />
      </div>
    );
  }
  return (
    <div className={`relative w-full bg-tnn-soft flex items-center justify-center ${large ? "h-60" : "h-44"}`}>
      <span className="font-display font-bold text-6xl text-tnn-purple/25 select-none">{project.initial}</span>
    </div>
  );
}

export function ChipFlow({ items, separator = "×" }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {items.map((item, i) => (
        <span key={i} className="inline-flex items-center gap-3">
          <span className="inline-flex items-center h-11 px-5 rounded-full bg-white border border-tnn-ink/10 text-sm font-semibold text-tnn-ink shadow-sm">
            {item}
          </span>
          {i < items.length - 1 &&
            (separator === "→" ? (
              <ArrowRight className="w-4 h-4 text-tnn-purple shrink-0" aria-hidden="true" />
            ) : (
              <span className="text-tnn-purple/50 font-semibold text-sm leading-none">{separator}</span>
            ))}
        </span>
      ))}
    </div>
  );
}
